import React, { useState } from "react";
import { useGetMasterDealerDashboard } from "@workspace/api-client-react";
import { 
  Download, 
  Filter, 
  Network, 
  Search, 
  TrendingUp, 
  UserPlus, 
  Users,
  CreditCard,
  MoreHorizontal
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatZAR } from "@/lib/formatters";
import { StatusBadge } from "@/components/StatusBadge";

export function MasterDealerDashboard() {
  const { data: dashboard, isLoading, error } = useGetMasterDealerDashboard();
  const [searchQuery, setSearchQuery] = useState("");

  if (isLoading) {
    return <div className="animate-pulse flex flex-col gap-6"><div className="h-10 bg-gray-200 w-1/4 rounded"></div><div className="h-40 bg-gray-200 w-full rounded"></div></div>;
  }

  if (error || !dashboard) {
    return <div className="text-red-500">Failed to load dashboard.</div>;
  }

  const filteredAgents = dashboard.subAgents.filter(agent => 
    agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    agent.id.toString().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#0B132B]">Network Overview</h1>
          <p className="text-gray-500 mt-1">Manage your sub-agents and monitor team performance across regions.</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="border-gray-300">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
          <Button className="bg-[#E6007E] hover:bg-[#c2006a] text-white shadow-md">
            <UserPlus className="w-4 h-4 mr-2" />
            Invite Sub-Agent
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-0 shadow-sm bg-white hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Total Team Active Clients</CardTitle>
            <Users className="h-4 w-4 text-[#0B132B]" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#0B132B]">{dashboard.totalActiveClients}</div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm bg-white hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Total Team Revenue</CardTitle>
            <div className="h-4 w-4 rounded-full bg-[#E6007E]/10 flex items-center justify-center">
              <span className="text-[10px] font-bold text-[#E6007E]">R</span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#0B132B]">{formatZAR(dashboard.totalTeamRevenueCents)}</div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-white hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Total Commission</CardTitle>
            <CreditCard className="h-4 w-4 text-[#E6007E]" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#E6007E]">{formatZAR(dashboard.totalCommissionCents)}</div>
            <p className="text-xs text-gray-500 flex items-center mt-1">
              Master cut + Direct sales
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-[#0B132B] text-white hover:shadow-md transition-shadow relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-10 -mt-10 blur-2xl"></div>
          <CardHeader className="flex flex-row items-center justify-between pb-2 relative z-10">
            <CardTitle className="text-sm font-medium text-gray-300">Active Sub-Agents</CardTitle>
            <Network className="h-4 w-4 text-white" />
          </CardHeader>
          <CardContent className="relative z-10">
            <div className="text-3xl font-bold">{dashboard.activeSubAgents}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="col-span-1 border-0 shadow-sm bg-gradient-to-br from-[#0B132B] to-[#1a2b5e] text-white">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-white/90">Master Commission Wallet</CardTitle>
            <CardDescription className="text-white/60">Current month distribution</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-col items-center justify-center p-6 bg-white/5 rounded-xl border border-white/10 backdrop-blur-sm">
              <span className="text-sm text-white/70 mb-1">Total Generated</span>
              <span className="text-4xl font-bold tracking-tight mb-2">{formatZAR(dashboard.totalCommissionCents)}</span>
              <Badge className="bg-[#00A859] hover:bg-[#00A859] text-white border-none text-[10px] uppercase font-bold px-2 py-0.5 rounded-full">
                Available for Payout
              </Badge>
            </div>
            <Button className="w-full bg-white text-[#0B132B] hover:bg-gray-100 font-semibold shadow-lg">
              Request Master Payout
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-4">
          <div>
            <CardTitle className="text-lg text-[#0B132B]">Team Management</CardTitle>
            <CardDescription>View and manage your network of sub-agents.</CardDescription>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
              <Input 
                placeholder="Search agents..." 
                className="pl-9 bg-gray-50 border-gray-200 focus-visible:ring-[#0B132B]"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Button variant="outline" size="icon" className="border-gray-200">
              <Filter className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow className="border-gray-100">
                <TableHead className="font-medium text-gray-500">Sub-Agent</TableHead>
                <TableHead className="font-medium text-gray-500 text-right">Orders</TableHead>
                <TableHead className="font-medium text-gray-500 text-right">Active</TableHead>
                <TableHead className="font-medium text-gray-500">Status</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAgents.map((agent) => (
                <TableRow key={agent.id} className="border-gray-100 hover:bg-gray-50/50">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9 border border-gray-100">
                        <AvatarFallback className="bg-gray-100 text-[#0B132B] text-xs font-semibold">
                          {agent.name.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-[#0B132B]">{agent.name}</p>
                        <p className="text-xs text-gray-500">{agent.openserveDealerCode ?? agent.dealerCode ?? "—"}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-medium">{agent.ordersSubmitted}</TableCell>
                  <TableCell className="text-right text-[#0B132B] font-semibold">{agent.activeClients}</TableCell>
                  <TableCell>
                    <StatusBadge status={agent.status} />
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-[#0B132B]">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>View Performance</DropdownMenuItem>
                        <DropdownMenuItem>Message Agent</DropdownMenuItem>
                        <DropdownMenuItem>View Commissions</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-red-600">Suspend Agent</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              
              {filteredAgents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="h-32 text-center text-gray-500">
                    No sub-agents found matching "{searchQuery}"
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </>
  );
}
